

const WPMLSelector = (rootContext = document) => {

    // Find all language selector instances in the given context
    const languageSelectorElements = Array.from(
        rootContext.querySelectorAll('.lang-select')
    );
    // Bail if none found
    if (!languageSelectorElements.length) return;

    languageSelectorElements.forEach((languageSelectorElement, index) => {
        // Prevent double-initialisation if this function is called again later
        if (languageSelectorElement.dataset.wpmlInitialised === '1') return;
        languageSelectorElement.dataset.wpmlInitialised = '1';

        // Cache key elements
        const triggerButton = languageSelectorElement.querySelector('.lang-select__button'); // button that toggles menu
        const menuElement   = languageSelectorElement.querySelector('.lang-select__menu');   // the dropdown/menu element
        const optionElements = Array.from(languageSelectorElement.querySelectorAll('.lang-select__option')); // clickable options

        // If required parts are missing, skip this instance
        if (!triggerButton || !menuElement) return;

        // Ensure unique ARIA association per instance
        // If the button already has a custom id, keep it; otherwise generate one
        const buttonId = (triggerButton.id && triggerButton.id !== 'langSelectButton')
            ? triggerButton.id
            : `langSelectButton-${index + 1}`;
        triggerButton.id = buttonId;
        menuElement.setAttribute('aria-labelledby', buttonId);

        // Track open/closed state for this instance
        let isMenuOpen = false;

        // Click anywhere outside the selector to close it
        const handleOutsideClick = (event) => {
            if (!languageSelectorElement.contains(event.target)) {
                setMenuOpen(false);
            }
        };

        // Open the menu with a height transition (0px -> scrollHeight -> auto)
        const openMenu = () => {
            // Update ARIA state
            triggerButton.setAttribute('aria-expanded', 'true');
            menuElement.setAttribute('aria-hidden', 'false');

            // Add classes that your CSS can hook into for transitions/pointer-events
            menuElement.classList.add('lang-select__menu--animating');
            menuElement.classList.add('lang-select__menu--open');

            // Set an explicit height so the browser can animate it
            menuElement.style.height = menuElement.scrollHeight + 'px';

            // When the transition ends, switch to `auto` so content can grow naturally
            const handleTransitionEnd = () => {
                menuElement.style.height = 'auto';
                menuElement.classList.remove('lang-select__menu--animating');
                menuElement.removeEventListener('transitionend', handleTransitionEnd);
            };
            menuElement.addEventListener('transitionend', handleTransitionEnd);

            // Close on outside click
            document.addEventListener('click', handleOutsideClick);
        };

        // Close the menu with a height transition (auto -> fixed px -> 0px)
        const closeMenu = () => {
            // Update ARIA state
            triggerButton.setAttribute('aria-expanded', 'false');
            menuElement.setAttribute('aria-hidden', 'true');

            // Mark as animating to disable pointer events (if desired in CSS)
            menuElement.classList.add('lang-select__menu--animating');

            // From `auto` we must set a fixed height first so the transition can run
            const currentHeight = menuElement.scrollHeight;
            menuElement.style.height = currentHeight + 'px';

            // Force a reflow so the browser applies the starting height before collapsing
            // eslint-disable-next-line no-unused-expressions
            menuElement.offsetHeight; // touch layout engine

            // Now animate to 0px and remove the "open" class
            menuElement.style.height = '0px';
            menuElement.classList.remove('lang-select__menu--open');

            // Clean up when the transition completes
            const handleTransitionEnd = () => {
                menuElement.classList.remove('lang-select__menu--animating');
                menuElement.removeEventListener('transitionend', handleTransitionEnd);
            };
            menuElement.addEventListener('transitionend', handleTransitionEnd);

            // Stop listening for outside clicks
            document.removeEventListener('click', handleOutsideClick);
        };

        // Toggle helper (or force a state by passing true/false)
        const setMenuOpen = (shouldOpen) => {
            const nextOpen = (typeof shouldOpen === 'boolean') ? shouldOpen : !isMenuOpen;
            if (nextOpen === isMenuOpen) return;
            isMenuOpen = nextOpen;
            if (isMenuOpen) {
                openMenu();
            } else {
                closeMenu();
            }
        };

        // Toggle on button click (and don’t let this click bubble to the document)
        triggerButton.addEventListener('click', (event) => {
            // Stop propagation so we don’t instantly close on our outside-click handler
            event.stopPropagation();
            setMenuOpen();
        });

        // Navigate when an option is chosen
        optionElements.forEach((optionElement) => {
            optionElement.addEventListener('click', () => {
                // Expecting WPML URL in data-url
                const targetUrl = optionElement.dataset.url;
                if (targetUrl) {
                    window.location.href = targetUrl;
                }
            });
        });

        // Keep the open menu height correct on viewport changes (e.g., responsive layout)
        window.addEventListener('resize', () => {
            if (!isMenuOpen) return;
            // Only adjust if we’re not currently animating
            if (!menuElement.classList.contains('lang-select__menu--animating')) {
                // If height is 'auto', temporarily set it to the new scrollHeight to avoid jumpiness
                menuElement.style.height = menuElement.scrollHeight + 'px';
            }
        });
    });
}
export default WPMLSelector;